import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2, FileText, FileSpreadsheet, FileImage, File as FileIcon,
  Upload, X, Trash2, Link2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axiosInstance from '../../api/axios';
import { validateFileSizes } from '../../utils/controlConstants';
import FileDropzone from '../FileDropzone';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatPeriodRange = (start, end) => {
  if (!start || !end) return 'Evergreen';
  const s = parseISO(String(start).split('T')[0]);
  const e = parseISO(String(end).split('T')[0]);
  return `${format(s, 'MMM d')} — ${format(e, 'MMM d')}, ${format(e, 'yyyy')}`;
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return { Icon: FileSpreadsheet, color: 'text-green-600' };
  if (['pdf'].includes(ext))                return { Icon: FileText,         color: 'text-red-600'   };
  if (['doc', 'docx'].includes(ext))        return { Icon: FileText,         color: 'text-blue-600'  };
  if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return { Icon: FileImage, color: 'text-purple-600' };
  return { Icon: FileIcon, color: 'text-gray-600' };
};

const TYPE_LABEL = {
  pbc:               'PBC / Evidence',
  population_report: 'Population Report',
  population:        'Population',
  verification:      'Verification',
  ipe:               'IPE',
};

const TYPE_COLOR = {
  pbc:               'bg-indigo-100 text-indigo-700',
  population_report: 'bg-amber-100  text-amber-700',
  population:        'bg-amber-100  text-amber-700',
  verification:      'bg-teal-100   text-teal-700',
  ipe:               'bg-purple-100 text-purple-700',
};

function TypeBadge({ type }) {
  const label = TYPE_LABEL[type] || (type || 'Evidence');
  const cls   = TYPE_COLOR[type] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
      {label}
    </span>
  );
}

function mappingCount(ev) {
  const m = ev.step_mapping;
  if (!m) return 0;
  try {
    const obj = typeof m === 'string' ? JSON.parse(m) : m;
    if (Array.isArray(obj)) return obj.length;
    return Object.keys(obj || {}).length;
  } catch { return 0; }
}

// ─── upload modal ────────────────────────────────────────────────────────────

function UploadModal({ controlUuid, evidencePeriods, onClose, onUploaded }) {
  const [evidenceType,  setEvidenceType]  = useState('pbc');
  const [periodStart,   setPeriodStart]   = useState('');
  const [periodEnd,     setPeriodEnd]     = useState('');
  const [uploading,     setUploading]     = useState(false);

  const existingDated = evidencePeriods.filter(p => p.testing_period_end);
  const smartStart = (() => {
    if (existingDated.length === 0) return new Date().toISOString().split('T')[0];
    const lastEnd = existingDated.reduce((latest, p) => {
      const d = String(p.testing_period_end).split('T')[0];
      return d > latest ? d : latest;
    }, '');
    if (!lastEnd) return new Date().toISOString().split('T')[0];
    const next = new Date(lastEnd + 'T00:00:00Z');
    next.setUTCDate(next.getUTCDate() + 1);
    return next.toISOString().split('T')[0];
  })();

  const selectValue =
    periodStart === '__evergreen__' ? '__evergreen__'
    : (periodStart && periodEnd && evidencePeriods.some(p => {
        const ps = String(p.testing_period_start).split('T')[0];
        const pe = String(p.testing_period_end).split('T')[0];
        return ps === periodStart && pe === periodEnd;
      })) ? `${periodStart}_${periodEnd}`
    : periodStart ? '__new__'
    : '';

  const periodReady =
    periodStart === '__evergreen__' ||
    (periodStart && periodEnd && periodEnd >= periodStart);

  const typeHint = {
    pbc:               'File is mapped to one or more test steps by the AI.',
    population_report: 'File is counted deterministically; drives the sample-size recommendation.',
    verification:      'File is consumed by the Completeness & Accuracy agents. Not mapped to a step.',
  }[evidenceType] || '';

  const handleFiles = async (files) => {
    const sizeErrors = validateFileSizes(files);
    if (sizeErrors.length > 0) { toast.error(sizeErrors.join(' ')); return; }
    setUploading(true);
    const userHash = localStorage.getItem('userHash');
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      if (periodStart !== '__evergreen__') {
        fd.append('testing_period_start', periodStart);
        fd.append('testing_period_end', periodEnd);
      }
      fd.append('evidence_type', evidenceType);
      try {
        await axiosInstance.post(`/api/sox-controls/${controlUuid}/evidence/upload`, fd, {
          headers: { Authorization: userHash || '', 'Content-Type': 'multipart/form-data' },
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setUploading(false);
    onUploaded();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4"
        onClick={e => e.stopPropagation()}>

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Upload Evidence</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Evidence type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload as</label>
            <select
              value={evidenceType}
              onChange={e => setEvidenceType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="pbc">Evidence (PBC) — mapped to test steps</option>
              <option value="population_report">Population report — counted for sampling</option>
              <option value="verification">Verification (GL, raw export, source docs) — C&amp;A checks</option>
            </select>
            {typeHint && <p className="text-[11px] text-gray-500 mt-1">{typeHint}</p>}
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Testing Period</label>
            <select
              value={selectValue}
              onChange={e => {
                const val = e.target.value;
                if (val === '__new__')       { setPeriodStart(smartStart); setPeriodEnd(''); }
                else if (val === '__evergreen__') { setPeriodStart('__evergreen__'); setPeriodEnd('__evergreen__'); }
                else if (val) { const [s, en] = val.split('_'); setPeriodStart(s); setPeriodEnd(en); }
                else          { setPeriodStart(''); setPeriodEnd(''); }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Choose a period…</option>
              {evidencePeriods.filter(p => p.testing_period_start && p.testing_period_end).map((p, i) => {
                const ps = String(p.testing_period_start).split('T')[0];
                const pe = String(p.testing_period_end).split('T')[0];
                return (
                  <option key={i} value={`${ps}_${pe}`}>
                    {formatPeriodRange(p.testing_period_start, p.testing_period_end)} ({p.file_count} files)
                  </option>
                );
              })}
              <option value="__evergreen__">Evergreen (all periods)</option>
              <option value="__new__">+ Create new period</option>
            </select>
          </div>

          {periodStart && periodStart !== '__evergreen__' && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                <input type="date" value={periodStart}
                  onChange={e => setPeriodStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                <input type="date" value={periodEnd}
                  onChange={e => setPeriodEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
          )}

          {periodStart && periodEnd && periodStart !== '__evergreen__' && periodEnd < periodStart && (
            <p className="text-sm text-red-600">End date must be on or after the start date.</p>
          )}

          {periodReady && (
            <FileDropzone
              multiple
              isUploading={uploading}
              label="Drop evidence files here or click to browse"
              compact
              onFilesSelected={handleFiles}
            />
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── delete confirm modal ────────────────────────────────────────────────────

function DeleteModal({ file, controlUuid, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    const userHash = localStorage.getItem('userHash');
    try {
      await axiosInstance.delete(`/api/sox-controls/${controlUuid}/evidence/${file.id}`, {
        headers: { Authorization: userHash || '' },
      });
      onDeleted();
      onClose();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Remove Evidence File</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Remove <strong>{file.filename}</strong> from the evidence store? You can re-upload it anytime.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
            {deleting ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function EvidenceStoreTab({ controlUuid, onPreviewFile }) {
  const [evidence,    setEvidence]    = useState([]);
  const [periods,     setPeriods]     = useState([]);
  const [execs,       setExecs]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [search,      setSearch]      = useState('');
  const [showUpload,  setShowUpload]  = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!controlUuid) return;
    setLoading(true);
    const userHash = localStorage.getItem('userHash');
    try {
      const [ev, pe, ex] = await Promise.all([
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`).then(r => r.data).catch(() => []),
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence/periods`).then(r => r.data).catch(() => []),
        axiosInstance.get(`/api/test-executions/control/${controlUuid}?include_history=1`, {
          headers: { Authorization: userHash || '' },
        }).then(r => r.data).catch(() => []),
      ]);
      setEvidence(Array.isArray(ev) ? ev : []);
      setPeriods(Array.isArray(pe) ? pe : []);
      setExecs(Array.isArray(ex) ? ex : []);
    } finally {
      setLoading(false);
    }
  }, [controlUuid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build "used in" map: filename → Set of run labels
  const usageByFile = {};
  execs.forEach(ex => {
    (ex.files_analyzed || []).forEach(fn => {
      const label = `RUN-${ex.test_run_number ?? '?'}${ex.test_run_name ? ` (${ex.test_run_name})` : ''}`;
      (usageByFile[fn] = usageByFile[fn] || new Set()).add(label);
    });
  });

  // Apply period filter then search
  const filtered = evidence.filter(ev => {
    if (periodFilter !== 'all') {
      if (periodFilter === 'evergreen') {
        if (ev.testing_period_start || ev.testing_period_end) return false;
      } else {
        const [fs, fe] = periodFilter.split('_');
        const es = ev.testing_period_start ? String(ev.testing_period_start).split('T')[0] : '';
        const ee = ev.testing_period_end   ? String(ev.testing_period_end).split('T')[0]   : '';
        if (es !== fs || ee !== fe) return false;
      }
    }
    if (search) {
      return ev.filename?.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  const totalFiles = evidence.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Evidence Store</h2>
          <p className="text-sm text-gray-500">
            All evidence for this control — type, period, mapping status and test run usage.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Upload Evidence
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Period:</label>
          <select
            value={periodFilter}
            onChange={e => setPeriodFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none min-w-[200px]"
          >
            <option value="all">All Periods ({totalFiles} files)</option>
            {periods.map((p, i) => {
              const key = p.testing_period_start && p.testing_period_end
                ? `${String(p.testing_period_start).split('T')[0]}_${String(p.testing_period_end).split('T')[0]}`
                : 'evergreen';
              const label = key === 'evergreen'
                ? `Evergreen (${p.file_count})`
                : `${formatPeriodRange(p.testing_period_start, p.testing_period_end)} (${p.file_count})`;
              return <option key={i} value={key}>{label}</option>;
            })}
          </select>
        </div>
        <div className="flex-1 max-w-xs">
          <input
            type="text"
            placeholder="Search files…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading evidence…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          {evidence.length === 0 ? (
            <>
              <p className="text-sm font-medium text-gray-700 mb-1">No evidence uploaded yet</p>
              <p className="text-xs text-gray-500 mb-4">Upload files using the button above.</p>
              <button
                onClick={() => setShowUpload(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <Upload className="h-4 w-4" /> Upload Evidence
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">No files match the current filter.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600 uppercase tracking-wide">
                <th className="px-5 py-3 text-left">File</th>
                <th className="px-5 py-3 text-left w-40">Type</th>
                <th className="px-5 py-3 text-left w-48">Period</th>
                <th className="px-5 py-3 text-left w-28">Mapping</th>
                <th className="px-5 py-3 text-left w-32">Uploaded</th>
                <th className="px-5 py-3 text-left">Used In</th>
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(ev => {
                const { Icon, color } = getFileIcon(ev.filename);
                const used   = Array.from(usageByFile[ev.filename] || []);
                const mapped = mappingCount(ev);
                return (
                  <tr key={ev.id} className="hover:bg-gray-50">
                    {/* File name */}
                    <td className="px-5 py-3">
                      <button
                        onClick={() => onPreviewFile?.({ id: ev.control_upload_id, name: ev.filename })}
                        className="flex items-center gap-2 hover:underline text-left"
                      >
                        <Icon className={`h-4 w-4 ${color} shrink-0`} />
                        <span className="text-gray-800 truncate max-w-[240px]" title={ev.filename}>
                          {ev.filename}
                        </span>
                      </button>
                    </td>
                    {/* Type badge */}
                    <td className="px-5 py-3">
                      <TypeBadge type={ev.evidence_type} />
                    </td>
                    {/* Period */}
                    <td className="px-5 py-3 text-gray-600 text-xs">
                      {formatPeriodRange(ev.testing_period_start, ev.testing_period_end)}
                    </td>
                    {/* Mapping */}
                    <td className="px-5 py-3">
                      {mapped > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700">
                          <Link2 className="h-3.5 w-3.5" /> {mapped} mapped
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Unmapped</span>
                      )}
                    </td>
                    {/* Uploaded date */}
                    <td className="px-5 py-3 text-gray-500 text-xs">
                      {ev.uploaded_at ? new Date(ev.uploaded_at).toLocaleDateString() : '—'}
                    </td>
                    {/* Used in */}
                    <td className="px-5 py-3">
                      {used.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">Not used yet</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {used.map(u => (
                            <span key={u}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                              {u}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {/* Delete */}
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setFileToDelete(ev)}
                        className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          controlUuid={controlUuid}
          evidencePeriods={periods}
          onClose={() => setShowUpload(false)}
          onUploaded={fetchAll}
        />
      )}
      {fileToDelete && (
        <DeleteModal
          file={fileToDelete}
          controlUuid={controlUuid}
          onClose={() => setFileToDelete(null)}
          onDeleted={fetchAll}
        />
      )}
    </div>
  );
}
